import { queryKeys } from '@/lib/query-keys';
import { trackEvent } from '@/lib/track-event';
import { useRuntimeConfig } from '@borradh-workspace/runtime-config/client';
import { Capacitor } from '@capacitor/core';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useFinalizeWhatsAppConnection } from '../api/finalize-whatsapp-connection';
import { openConnectHandoff } from '../lib/open-connect-handoff';
import { useFacebookSdk } from './use-facebook-sdk';

type WaEmbeddedSignupMessage = {
  type: 'WA_EMBEDDED_SIGNUP';
  event?: string;
  data?: { waba_id?: string; phone_number_id?: string };
  version?: number;
};

interface UseWhatsAppEmbeddedSignupOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

/**
 * Opens Meta's WhatsApp Embedded Signup popup in Coexistence mode and
 * finalizes the connection server-side when the customer completes it.
 *
 * Flow:
 *   1. FB SDK is loaded + initialized in the background.
 *   2. A postMessage listener captures `waba_id` from Meta's popup.
 *   3. `launch()` calls `FB.login` with the Coexistence extras.
 *   4. On success, POSTs `{ code, wabaId }` to `/integrations/whatsapp/finalize`.
 *
 * Important: `launch()` must be called synchronously from a user gesture —
 * do not await anything before calling it or popup blockers will kill the
 * window.
 */
export const useWhatsAppEmbeddedSignup = (
  options?: UseWhatsAppEmbeddedSignupOptions
) => {
  const isNative = Capacitor.isNativePlatform();
  const { fb, isReady } = useFacebookSdk();
  const [isLaunching, setIsLaunching] = useState(false);
  const wabaIdRef = useRef<string | null>(null);
  // Diagnostic-only: not read for control flow, just to report signal
  // ordering to PostHog (see ENG-1031). Safe to remove once that's answered.
  const codeReceivedRef = useRef(false);
  const { whatsappEmbeddedSignupConfigId: configId } = useRuntimeConfig();
  const queryClient = useQueryClient();

  const { finalizeAsync, isFinalizing } = useFinalizeWhatsAppConnection({
    onSuccess: () => {
      setIsLaunching(false);
      trackEvent('whatsapp_embedded_signup_finalize_succeeded');
      options?.onSuccess?.();
    },
    onError: (err) => {
      setIsLaunching(false);
      trackEvent('whatsapp_embedded_signup_finalize_failed');
      options?.onError?.(err);
    },
  });

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      // Strict origin check — `endsWith('facebook.com')` would also match
      // `https://evilfacebook.com`. Require the exact host or a dotted suffix.
      let host: string;
      try {
        host = new URL(event.origin).hostname;
      } catch {
        return;
      }
      if (host !== 'facebook.com' && !host.endsWith('.facebook.com')) return;
      try {
        const data =
          typeof event.data === 'string'
            ? (JSON.parse(event.data) as WaEmbeddedSignupMessage)
            : (event.data as WaEmbeddedSignupMessage);
        if (data?.type !== 'WA_EMBEDDED_SIGNUP') return;
        trackEvent('whatsapp_embedded_signup_postmessage_received', {
          meta_event: data.event ?? null,
          has_waba_id: Boolean(data.data?.waba_id),
          code_already_received: codeReceivedRef.current,
        });
        if (data.data?.waba_id) {
          wabaIdRef.current = data.data.waba_id;
        }
      } catch {
        /* ignore non-JSON messages */
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  const launch = useCallback(() => {
    // The Capacitor WebView's origin (`https://localhost`) can never be on
    // the Meta app's JS-SDK allowed domains, so FB.login there always dies
    // with "unknown host domain". Hand the flow to the system browser on the
    // real app domain instead; /connect/whatsapp runs the popup there and the
    // hand-off refreshes the connection state when the user comes back.
    if (isNative) {
      void openConnectHandoff({
        path: '/connect/whatsapp',
        queryClient,
        queryKey: queryKeys.integrations.whatsapp(),
        providerLabel: 'WhatsApp',
      });
      return;
    }

    if (!fb) {
      toast.error('Facebook is still loading — try again in a moment');
      return;
    }
    if (!configId) {
      toast.error('WhatsApp Embedded Signup is not configured');
      return;
    }

    wabaIdRef.current = null;
    codeReceivedRef.current = false;
    setIsLaunching(true);
    trackEvent('whatsapp_embedded_signup_launched');

    // FB SDK rejects AsyncFunction callbacks — keep this sync and let the
    // mutation run detached. onSuccess / onError are wired on the mutation.
    fb.login(
      (response) => {
        const code = response?.authResponse?.code;
        codeReceivedRef.current = true;
        if (!code) {
          setIsLaunching(false);
          trackEvent('whatsapp_embedded_signup_fb_callback_no_code');
          return;
        }
        const wabaId = wabaIdRef.current;
        if (!wabaId) {
          setIsLaunching(false);
          // If this fires for a real customer, the WABA-id postMessage had
          // not arrived yet when the FB.login code callback ran — the
          // signal-ordering race under investigation in ENG-1031.
          trackEvent('whatsapp_embedded_signup_missing_waba_at_code');
          toast.error(
            'WhatsApp Business Account was not returned from Meta — please try again'
          );
          return;
        }
        trackEvent('whatsapp_embedded_signup_finalize_started');
        finalizeAsync({ code, wabaId }).catch(() => {
          /* surfaced via the mutation's onError toast */
        });
      },
      {
        config_id: configId,
        response_type: 'code',
        override_default_response_type: true,
        extras: {
          setup: {},
          featureType: 'whatsapp_business_app_onboarding',
          sessionInfoVersion: '3',
        },
      }
    );
  }, [fb, configId, finalizeAsync, isNative, queryClient]);

  return {
    launch,
    // Native never needs the FB SDK — the system browser does the work.
    isReady: isNative || isReady,
    isBusy: isLaunching || isFinalizing,
  };
};
